import { useMemo, useState } from 'react';

import { Input } from './Input';
import { cn } from '@/lib/cn';
import { ICON_MAP, ICON_PICKER_NAMES as ALL_ICON_NAMES } from './icon-picker-icons';

/**
 * IconPicker — picker reusável de ícones do lucide-react (R5.2 Bloco I).
 *
 * Lista CURADA de ~70 ícones úteis pra contexto IGM (atividades, comunicação,
 * status, comercial, manutenção). Filtro por texto que matche o nome.
 *
 * Decisão #7 do status-r51r52-progresso.md: usa `lucide-react` (já no bundle),
 * com curadoria — evita usuário escolher ícone fora do design system.
 * Trade-off: lista de 70 vs ~1000+ disponíveis — se virar dor, abrir.
 */

export interface IconPickerProps {
  /** Nome do ícone selecionado (chave em ICON_MAP). Null = nenhum. */
  value: string | null;
  /** Chamado quando user seleciona ícone. Null = limpa. */
  onChange: (iconName: string | null) => void;
  /** Permite limpar a seleção via botão "—" (default true). */
  clearable?: boolean;
  /** Classes extras pro container. */
  className?: string;
}

export function IconPicker({ value, onChange, clearable = true, className }: IconPickerProps) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ALL_ICON_NAMES;
    return ALL_ICON_NAMES.filter((n) => n.toLowerCase().includes(q));
  }, [query]);

  return (
    <div data-testid="icon-picker" className={cn('space-y-2', className)}>
      <div className="flex items-center gap-2">
        <Input
          data-testid="icon-picker-search"
          placeholder="Buscar ícone..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1"
        />
        {clearable && value && (
          <button
            type="button"
            data-testid="icon-picker-clear"
            onClick={() => onChange(null)}
            className="text-xs text-ink-3 hover:text-red transition-colors"
            title="Remover ícone"
          >
            ✕ limpar
          </button>
        )}
      </div>
      <div
        className="grid grid-cols-8 sm:grid-cols-10 gap-1 max-h-40 overflow-y-auto p-1 rounded-lg bg-paper dark:bg-paper-dark border border-line dark:border-line-dark"
        role="listbox"
        aria-label="Seleção de ícone"
      >
        {filtered.length === 0 ? (
          <p className="col-span-full text-xs text-ink-3 italic text-center py-3">
            Nenhum ícone encontrado pra &ldquo;{query}&rdquo;
          </p>
        ) : (
          filtered.map((name) => {
            const Icon = ICON_MAP[name];
            const active = value === name;
            return (
              <button
                key={name}
                type="button"
                data-testid={`icon-picker-option-${name}`}
                data-active={active || undefined}
                aria-pressed={active}
                onClick={() => onChange(name)}
                title={name}
                className={cn(
                  'aspect-square flex items-center justify-center rounded-md transition-colors',
                  active
                    ? 'bg-red text-white'
                    : 'text-ink-2 dark:text-ink-dark-2 hover:bg-red-soft dark:hover:bg-red-soft-dark hover:text-red-deep'
                )}
              >
                <Icon className="w-4 h-4" aria-hidden="true" />
              </button>
            );
          })
        )}
      </div>
      {value && (
        <p className="text-xs text-ink-3 dark:text-ink-dark-3" data-testid="icon-picker-selected">
          Ícone selecionado:{' '}
          <strong className="text-ink-1 dark:text-ink-dark-1">{value}</strong>
        </p>
      )}
    </div>
  );
}

// Helpers + constantes movidos pra `icon-picker-icons.tsx` pra preservar
// react-refresh/only-export-components em IconPicker.tsx. Re-exporte do
// index.ts via `./icon-picker-icons`.
